import { useState } from "react";
import { useAccount, useChainId, usePublicClient, useWriteContract } from "wagmi";
import { Atelier1of1Abi } from "@atelier/abis";
import { api } from "@/lib/api";
import { byId } from "@/lib/chains";
import { useAuthStore } from "@/stores/auth";
import { RequireAuth } from "@/components/RequireAuth";
import { ExplorerLink } from "@/components/ExplorerLink";

export function Mint1of1() {
  const { address } = useAccount();
  const chainId = useChainId();
  const chain = byId(chainId);
  const pc = usePublicClient({ chainId });
  const { writeContractAsync, isPending } = useWriteContract();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [lastTx, setLastTx] = useState<`0x${string}` | null>(null);

  const contract = chain?.contracts.atelier1of1;
  const disabled = !contract || contract === "0x0000000000000000000000000000000000000000";

  const mint = async () => {
    if (!address || !file || !contract) return;
    setLastTx(null);
    setStatus("Uploading image to IPFS…");
    const { cid: imageCid } = await api.uploadFile(file);
    setStatus("Pinning metadata…");
    const metadata = {
      name,
      description,
      image: `ipfs://${imageCid}`,
    };
    const { cid: metaCid } = await api.uploadJson(metadata);
    setStatus("Waiting for signature…");
    const hash = await writeContractAsync({
      address: contract,
      abi: Atelier1of1Abi,
      functionName: "mint",
      args: [`ipfs://${metaCid}`],
      chainId,
    });
    setLastTx(hash);
    setStatus("Confirming…");
    if (pc) await pc.waitForTransactionReceipt({ hash });
    setStatus("Minted!");
  };

  return (
    <div className="mx-auto max-w-xl px-4 py-10">
        <h1 className="mb-1 text-2xl font-bold">Mint a 1/1</h1>
        <p className="mb-3 text-sm text-muted-foreground">
          Goes straight into the shared <code>Atelier1of1</code> contract on {chain?.name}. You keep ownership.
        </p>
        {chain && contract && contract !== "0x0000000000000000000000000000000000000000" && (
          <div className="mb-6 text-xs text-muted-foreground">
            contract: <ExplorerLink chain={chain} kind="address" value={contract} />
          </div>
        )}

        {disabled ? (
          <div className="card border-amber-300 bg-amber-50 text-sm text-amber-900 dark:border-amber-700/40 dark:bg-amber-900/20 dark:text-amber-300">
            The 1/1 contract isn't deployed on this chain yet.
          </div>
        ) : (
          <div className="card space-y-4">
            <label className="flex flex-col gap-1">
              <span className="label">Name</span>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="label">Description</span>
              <textarea className="input min-h-[100px]" value={description} onChange={(e) => setDescription(e.target.value)} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="label">Artwork</span>
              <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
              {file && <span className="text-xs text-muted-foreground">{file.name} · {Math.round(file.size / 1024)} KB</span>}
            </label>
            <MintActionGate
              canMint={!!file && !!name}
              isPending={isPending}
              onMint={mint}
              status={status}
              setStatus={setStatus}
              lastTx={lastTx}
              chain={chain}
            />
          </div>
        )}
    </div>
  );
}

function MintActionGate({
  canMint,
  isPending,
  onMint,
  status,
  setStatus,
  lastTx,
  chain,
}: {
  canMint: boolean;
  isPending: boolean;
  onMint: () => Promise<void>;
  status: string | null;
  setStatus: (s: string | null) => void;
  lastTx: `0x${string}` | null;
  chain: ReturnType<typeof byId>;
}) {
  const isAuthed = useAuthStore((s) => s.isAuthed());
  if (!isAuthed) {
    return (
      <div className="space-y-3">
        <div className="text-xs text-muted-foreground">
          Minting requires a wallet signature and an IPFS upload. Sign in to continue.
        </div>
        <RequireAuth>
          {/* RequireAuth renders a sign-in card when not authed; children are hidden until auth completes. */}
          <></>
        </RequireAuth>
      </div>
    );
  }
  return (
    <>
      <button
        type="button"
        className="btn-primary w-full"
        disabled={isPending || !canMint}
        onClick={() => void onMint().catch((e) => setStatus(`Error: ${(e as Error).message}`))}
      >
        {isPending ? "Working…" : "Mint"}
      </button>
      {status && (
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <span>{status}</span>
          {lastTx && chain && (
            <ExplorerLink chain={chain} kind="tx" value={lastTx} label="view tx" className="text-xs" />
          )}
        </div>
      )}
    </>
  );
}
